import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listCabLocations } from '../../api/cabsPublic';

const buildLabel = (loc) => {
  const suffix = loc.state || loc.city || '';
  return suffix ? `${loc.name} (${suffix})` : loc.name;
};

const LocationAutocomplete = ({ label, placeholder, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const optionMap = useMemo(() => {
    const map = new Map();
    options.forEach((loc) => {
      map.set(loc.id, loc);
    });
    return map;
  }, [options]);

  useEffect(() => {
    setLoading(true);
    listCabLocations()
      .then((res) => setOptions(res.data || res || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setLoading(true);
      listCabLocations({ search: query })
        .then((res) => setOptions(res.data || res || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const selected = value ? optionMap.get(value) : null;
  const inputValue = open ? query : selected ? buildLabel(selected) : query;

  const handleSelect = (loc) => {
    onChange(loc.id);
    setQuery(buildLabel(loc));
    setOpen(false);
  };

  return (
    <div className="relative">
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      <input
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && (
        <div className="absolute z-10 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="max-h-52 overflow-auto py-1 text-sm">
            {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading...</div>}
            {!loading && options.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-400">No locations found</div>
            )}
            {!loading &&
              options.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(loc)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                >
                  <span>{buildLabel(loc)}</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
